import { createTheme } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// Zealous Tech Corp — central design system
// Brand colours: primary blue #0c83c8, accent orange #fc7a46.
// Typography: Inter (loaded in public/index.html) for a clean, corporate feel.
// ---------------------------------------------------------------------------

export const brand = {
  blue: "#0c83c8",
  blueDark: "#0a6aa3",
  blueLight: "#3a9ad4",
  orange: "#fc7a46",
  orangeDark: "#e55e2c",
  orangeLight: "#fd9469",
  navy: "#0f2740",
  slate: "#475569",
  ink: "#1e293b",
  bg: "#f6f9fc",
  bgAlt: "#eef3f9",
  white: "#ffffff",
};

// Reusable gradients
export const gradients = {
  brand: `linear-gradient(135deg, ${brand.blue} 0%, ${brand.orange} 100%)`,
  hero: `linear-gradient(135deg, #0a6aa3 0%, ${brand.blue} 45%, #1597dd 100%)`,
  soft: `linear-gradient(135deg, #f8fbff 0%, #eaf2fb 100%)`,
  navy: `linear-gradient(135deg, #0d2438 0%, #123c5c 100%)`,
  // Layered radial "mesh" used behind the hero / dark sections for depth
  mesh: `radial-gradient(1200px 600px at 12% 8%, rgba(21,151,221,0.55) 0%, transparent 55%),
         radial-gradient(900px 500px at 88% 12%, rgba(252,122,70,0.38) 0%, transparent 55%),
         radial-gradient(1000px 700px at 60% 110%, rgba(10,106,163,0.55) 0%, transparent 60%),
         linear-gradient(135deg, #0a2b45 0%, #0c4c78 55%, #0f2740 100%)`,
};

// Consistent elevation scale — use instead of ad-hoc shadow values
export const shadows = {
  sm: "0 2px 8px rgba(15,39,64,0.06)",
  card: "0 8px 24px rgba(15,39,64,0.08)",
  cardHover: "0 22px 48px rgba(15,39,64,0.14)",
  brand: "0 14px 34px rgba(12,131,200,0.28)",
  accent: "0 14px 34px rgba(252,122,70,0.30)",
};

// Shared card interaction preset — keeps every card on-brand & consistent
export const cardHover = {
  transition: "transform .3s ease, box-shadow .3s ease, border-color .3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: shadows.cardHover,
    borderColor: "rgba(12,131,200,0.35)",
  },
};

const FONT = "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif";

const theme = createTheme({
  palette: {
    primary: {
      main: brand.blue,
      light: brand.blueLight,
      dark: brand.blueDark,
      contrastText: "#ffffff",
    },
    secondary: {
      main: brand.orange,
      light: brand.orangeLight,
      dark: brand.orangeDark,
      contrastText: "#ffffff",
    },
    background: {
      default: brand.bg,
      paper: "#ffffff",
    },
    text: {
      primary: brand.ink,
      secondary: brand.slate,
    },
    success: { main: "#16a34a" },
    error: { main: "#ef4444" },
  },
  typography: {
    fontFamily: FONT,
    h1: { fontFamily: FONT, fontWeight: 800, letterSpacing: "-0.02em" },
    h2: { fontFamily: FONT, fontWeight: 800, letterSpacing: "-0.02em" },
    h3: { fontFamily: FONT, fontWeight: 700, letterSpacing: "-0.01em" },
    h4: { fontFamily: FONT, fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontFamily: FONT, fontWeight: 700 },
    h6: { fontFamily: FONT, fontWeight: 600 },
    subtitle1: { fontFamily: FONT, fontWeight: 500 },
    subtitle2: { fontFamily: FONT, fontWeight: 500 },
    body1: { fontFamily: FONT, lineHeight: 1.7 },
    body2: { fontFamily: FONT, lineHeight: 1.7 },
    button: { fontFamily: FONT, fontWeight: 600, textTransform: "none" },
    overline: { fontFamily: FONT, fontWeight: 700, letterSpacing: "0.12em" },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 600,
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 22,
          paddingRight: 22,
          boxShadow: "none",
        },
        containedPrimary: {
          "&:hover": { boxShadow: "0 10px 24px rgba(12,131,200,0.30)" },
        },
        containedSecondary: {
          "&:hover": { boxShadow: "0 10px 24px rgba(252,122,70,0.30)" },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(15,39,64,0.08)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 18, backgroundImage: "none" },
      },
    },
    // AppBar extends Paper — force square corners so the fixed navbar never curves
    MuiAppBar: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Consistent, visible focus ring for keyboard users across the site
        "a:focus-visible, button:focus-visible, [role='button']:focus-visible, .MuiButtonBase-root:focus-visible":
          {
            outline: `3px solid ${brand.orange}`,
            outlineOffset: "2px",
            borderRadius: 8,
          },
      },
    },
  },
});

export default theme;
